import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  DatePicker,
  Select,
  Input,
  Tag,
  Popconfirm,
  Space,
} from 'antd';
import { PlusOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import {
  create_employee_availability,
  get_employee_availability,
  delete_employee_availability,
} from '../../../utils/shift_availability_api';
import { get_employee } from '../../../utils/api';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/DataTable';
import PageHeader, { EmptyState } from '@/components/PageHeader';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { formatDate } from '@/utils/datetime';
import HostedCard from '@/components/HostedCard';
import HeaderActions from '@/components/HeaderActions';
import PageModal from '@/components/PageModal';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

function EmployeeAvailability({ isEmbedded = false, active = true }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [form] = Form.useForm();

  // Availability type — one status colour each: green = available, amber = planned/approved leave,
  // red = out of action (sick or unavailable), blue = in training, grey = neutral time off.
  const typeStyle = {
    AVAILABLE: { color: 'success', label: 'Available' },
    LEAVE: { color: 'warning', label: 'On leave' },
    SICK: { color: 'error', label: 'Sick leave' },
    TRAINING: { color: 'processing', label: 'Training' },
    VACATION: { color: 'default', label: 'Vacation' },
    UNAVAILABLE: { color: 'error', label: 'Unavailable' },
  };

  const loadAvailabilityData = async () => {
    setLoading(true);
    try {
      await get_employee_availability({}, (res) => {
        if (res.data) {
          setAvailabilityData(res.data);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading availability:', error);
      toast.error('Failed to load availability data');
      setLoading(false);
    }
  };

  const loadEmployeeList = async () => {
    try {
      await get_employee((res) => {
        if (res?.data?.data) {
          setEmployeeList(res.data.data);
        }
      });
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Failed to load employees');
    }
  };

  useEffect(() => {
    loadAvailabilityData();
    loadEmployeeList();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        employee_id: values.employee_id,
        start_date: values.dateRange[0].format('YYYY-MM-DD'),
        end_date: values.dateRange[1].format('YYYY-MM-DD'),
        availability_type: values.availability_type,
        reason: values.reason || '',
        is_full_day: true,
      };

      await create_employee_availability(payload, (res) => {
        toast.success(res.message || 'Availability record created successfully');
        setIsModalOpen(false);
        form.resetFields();
        loadAvailabilityData();
      });
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error('Failed to create availability record');
    }
  };

  const handleDelete = async (id) => {
    try {
      await delete_employee_availability(id, (res) => {
        toast.success(res.message || 'Availability record deleted successfully');
        loadAvailabilityData();
      });
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error('Failed to delete availability record');
    }
  };

  const allColumns = [
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: '20%',
      defaultVisible: true,
      sortType: 'string',
    },
    {
      title: 'Type',
      dataIndex: 'availability_type',
      key: 'availability_type',
      width: '15%',
      align: 'center',
      defaultVisible: true,
      sortType: 'string',
      render: (type) => {
        const st = typeStyle[type] || { color: 'default', label: type };
        return (
          <Tag color={st.color} style={{ width: 112, margin: 0, textAlign: 'center', whiteSpace: 'nowrap' }}>
            {st.label}
          </Tag>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      width: '15%',
      align: 'center',
      defaultVisible: true,
      sortType: 'date',
      render: (date) => formatDate(date),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      width: '15%',
      align: 'center',
      defaultVisible: true,
      sortType: 'date',
      render: (date) => formatDate(date),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: '25%',
      defaultVisible: true,
      sortType: 'string',
      render: (text) => text || '-',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: '10%',
      align: 'center',
      defaultVisible: true,
      render: (_, record) => (
        <Popconfirm
          title="Delete Availability"
          description="Are you sure you want to delete this availability record?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button danger size="small" icon={<DeleteOutlined style={{ fontSize: '20px' }} />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const addButton = (
    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
      Add Availability
    </Button>
  );

  return (
    <HostedCard
      hosted={isEmbedded}
      title={
        <PageHeader
          compact
          icon={<Icons.Calendar size={22} />}
          title={t('Employee Availability Management')}
          description="Leave, sickness, training and other unavailability windows per employee."
        />
      }
      extra={addButton}
    >
      {/* hosted in Shifts: Add Availability goes in that page's header, only while this tab is open */}
      {isEmbedded && active && <HeaderActions>{addButton}</HeaderActions>}
      <DataTable
        tableKey="employee_availability_config"
        searchable
        searchKeys={['employee_name', 'availability_type', 'reason']}
        searchPlaceholder="Search employee..."
        columnConfig
        showCount={false}
        exportable
        exportFilename="employee_availability"
        locale={{ emptyText: <EmptyState title="No availability records" description="Record leave, training or other unavailability for an employee." actionLabel="Add Availability" onAction={() => setIsModalOpen(true)} /> }}
        data={availabilityData}
        columns={allColumns}
        loading={loading}
        rowKey="id"
        bordered
        height={null}
        scrollX="max-content"
      />

      <PageModal
        open={isModalOpen}
        icon={<Icons.Calendar size={22} />}
        title="Add Employee Availability"
        description="Record leave, training or other unavailability for an employee."
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        width={720}
        destroyOnHidden
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="employee_id"
            label="Employee"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select employee"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {employeeList.map((emp) => (
                <Option key={emp.employee_id} value={emp.employee_id}>
                  {emp.employee_name || emp.username}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="availability_type"
            label="Availability Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select placeholder="Select type">
              <Option key="AVAILABLE" value="AVAILABLE">
                Available
              </Option>
              <Option key="LEAVE" value="LEAVE">
                On Leave
              </Option>
              <Option key="SICK" value="SICK">
                Sick Leave
              </Option>
              <Option key="TRAINING" value="TRAINING">
                Training
              </Option>
              <Option key="VACATION" value="VACATION">
                Vacation
              </Option>
              <Option key="UNAVAILABLE" value="UNAVAILABLE">
                Unavailable
              </Option>
            </Select>
          </Form.Item>

          <Form.Item name="reason" label="Reason">
            <TextArea rows={3} placeholder="Enter reason for unavailability..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Availability Record
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </PageModal>
    </HostedCard>
  );
}

export default EmployeeAvailability;
